import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const features = [
  {
    icon: "ri-vidicon-line",
    title: "HD Video Calls",
    desc: "Crystal-clear 1080p video calls with anyone in the world — buyers, suppliers, friends, or family. Works on WiFi and mobile data.",
    color: "bg-red-50 text-[#C41E3A]",
  },
  {
    icon: "ri-message-3-line",
    title: "Instant Messaging",
    desc: "Send text, voice messages, photos, videos, and files instantly. Group chats for supplier teams and reseller communities.",
    color: "bg-amber-50 text-[#D4AF37]",
  },
  {
    icon: "ri-group-line",
    title: "Group Video Calls",
    desc: "Host group video calls with up to 100 participants. Perfect for supplier meetings, reseller training, and team collaboration.",
    color: "bg-red-50 text-[#C41E3A]",
  },
  {
    icon: "ri-translate-2",
    title: "Real-Time Translation",
    desc: "Built-in AI translation for English, French, Chinese, Spanish and 50+ languages. Break language barriers with suppliers worldwide.",
    color: "bg-amber-50 text-[#D4AF37]",
  },
  {
    icon: "ri-file-transfer-line",
    title: "File & Document Sharing",
    desc: "Share product catalogs, invoices, contracts, and images up to 2GB. All files encrypted end-to-end.",
    color: "bg-red-50 text-[#C41E3A]",
  },
  {
    icon: "ri-shield-keyhole-line",
    title: "End-to-End Encryption",
    desc: "All messages and calls are fully encrypted. Your business conversations stay private and secure at all times.",
    color: "bg-amber-50 text-[#D4AF37]",
  },
  {
    icon: "ri-store-2-line",
    title: "In-Chat Shopping",
    desc: "Browse and order Crown Prime Mall products directly inside a chat. Share product links, negotiate prices, and confirm orders.",
    color: "bg-red-50 text-[#C41E3A]",
  },
  {
    icon: "ri-notification-3-line",
    title: "Order Notifications",
    desc: "Get real-time alerts for order updates, shipping status, and supplier messages — all in one place.",
    color: "bg-amber-50 text-[#D4AF37]",
  },
];

const stats = [
  { value: "180+", label: "Countries Supported" },
  { value: "50+", label: "Languages" },
  { value: "100", label: "Max Group Call Size" },
  { value: "2GB", label: "Max File Share" },
];

const testimonials = [
  {
    name: "Jessica M.",
    role: "TikTok Reseller, USA",
    avatar: "https://public.readdy.ai/ai/img_res/1533ca41dc45638f2eda28ad144cfe1c.jpg",
    text: "CrownChat changed how I work with my suppliers. I can video call them directly, see the products live, and negotiate prices — all without leaving the app. It's like WhatsApp but built for business.",
  },
  {
    name: "Marcus T.",
    role: "YouTube Creator, Canada",
    avatar: "https://readdy.ai/api/search-image?query=young%20man%20smiling%20professional%20headshot%20clean%20white%20background%20natural%20lighting&width=80&height=80&seq=tst2&orientation=squarish",
    text: "The group video call feature is incredible. I run weekly calls with my supplier team in China and my reseller network in Canada — all in one app. The translation feature is a game changer.",
  },
  {
    name: "Linda K.",
    role: "Factory Supplier, Shenzhen",
    avatar: "https://readdy.ai/api/search-image?query=asian%20woman%20smiling%20professional%20headshot%20clean%20white%20background%20natural%20lighting&width=80&height=80&seq=tst3&orientation=squarish",
    text: "We use CrownChat to communicate with all our USA and Canada buyers. The real-time translation means no more misunderstandings. Our order accuracy improved by 40% since we switched.",
  },
];

const CrownChatPage = () => {
  const [email, setEmail] = useState("");
  const [notified, setNotified] = useState(false);

  useEffect(() => {
    document.title = "CrownChat – HD Video Calls & Messaging for USA & Canada | Crown Prime Mall";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "CrownChat is the all-in-one communication app for Crown Prime Mall buyers, suppliers, and resellers. HD video calls, real-time translation in 50+ languages, group calls up to 100 people. Coming to App Store & Google Play.");
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-crownchat";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "CrownChat",
      "applicationCategory": "CommunicationApplication",
      "operatingSystem": "iOS, Android",
      "description": "CrownChat is the all-in-one communication app for Crown Prime Mall buyers, suppliers, and resellers. HD video calls, instant messaging, real-time translation — available on iPhone and Android in USA & Canada.",
      "url": siteUrl + "/crownchat",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Crown Prime Mall",
        "url": siteUrl
      },
      "featureList": [
        "HD Video Calls",
        "Instant Messaging",
        "Group Video Calls up to 100 participants",
        "Real-Time Translation in 50+ languages",
        "File & Document Sharing up to 2GB",
        "End-to-End Encryption",
        "In-Chat Shopping",
        "Order Notifications"
      ],
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "CrownChat", "item": siteUrl + "/crownchat" }
        ]
      }
    });
    if (!document.getElementById("schema-crownchat")) {
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById("schema-crownchat");
      if (el) el.remove();
    };
  }, []);

  const handleNotify = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) setNotified(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="bg-[#1a0a0a] text-white text-xs text-center py-2 px-4">
        CrownChat — Coming to App Store &amp; Google Play for USA &amp; Canada &nbsp;|&nbsp; Sign up to be notified at launch
      </div>

      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 cursor-pointer whitespace-nowrap flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
              alt="Crown Prime Mall"
              className="h-9 w-9 rounded-full object-cover"
            />
            <div>
              <div className="font-bold text-sm text-[#C41E3A] leading-tight">Crown Prime Mall</div>
              <div className="text-[10px] text-gray-400">USA &amp; Canada Marketplace</div>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm text-gray-500">
            <a href="#features" className="hover:text-[#C41E3A] cursor-pointer whitespace-nowrap transition-colors">Features</a>
            <a href="#download" className="hover:text-[#C41E3A] cursor-pointer whitespace-nowrap transition-colors">Download</a>
            <a href="#testimonials" className="hover:text-[#C41E3A] cursor-pointer whitespace-nowrap transition-colors">Reviews</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-gray-500 hover:text-[#C41E3A] cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-arrow-left-line mr-1"></i>Back to Mall
            </Link>
            <a
              href="#download"
              className="bg-[#C41E3A] text-white text-xs px-4 py-2 rounded-full font-semibold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap"
            >
              Download App
            </a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#1a0a0a] pt-20 pb-0">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20background%20with%20glowing%20red%20and%20gold%20network%20connection%20lines%20representing%20global%20communication%2C%20digital%20technology%20concept%2C%20deep%20dark%20background%20with%20subtle%20geometric%20patterns%2C%20cinematic%20lighting&width=1400&height=700&seq=ccbg1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a0a0a]/60 via-transparent to-[#1a0a0a]" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-[#C41E3A]/20 border border-[#C41E3A]/40 text-[#D4AF37] text-xs font-semibold px-4 py-2 rounded-full mb-6">
            <i className="ri-message-3-line"></i>
            Introducing CrownChat — The Business Communication App
          </div>

          {/* Headline */}
          <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-6 max-w-4xl mx-auto">
            Video Call Anyone
            <span className="text-[#D4AF37]"> in the World.</span>
            <br />
            <span className="text-[#C41E3A]">Buy. Sell. Connect.</span>
          </h1>
          <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
            CrownChat is the all-in-one communication app for Crown Prime Mall buyers, suppliers, and resellers. HD video calls, instant messaging, real-time translation — available on iPhone and Android in USA &amp; Canada.
          </p>

          {/* Download Buttons */}
          <div id="download" className="flex flex-wrap gap-4 justify-center mb-16">
            <a
              href="#"
              className="flex items-center gap-3 bg-white text-[#1a0a0a] px-6 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors cursor-pointer group"
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-apple-line text-2xl"></i>
              </div>
              <div className="text-left">
                <div className="text-[10px] text-gray-500 leading-none">Download on the</div>
                <div className="text-base font-extrabold leading-tight">App Store</div>
              </div>
            </a>
            <a
              href="#"
              className="flex items-center gap-3 bg-white text-[#1a0a0a] px-6 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors cursor-pointer group"
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-google-play-line text-2xl text-green-600"></i>
              </div>
              <div className="text-left">
                <div className="text-[10px] text-gray-500 leading-none">Get it on</div>
                <div className="text-base font-extrabold leading-tight">Google Play</div>
              </div>
            </a>
            <a
              href="https://wa.me/1234567890"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-[#25D366] text-white px-6 py-4 rounded-xl font-semibold hover:bg-[#1ebe5d] transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-whatsapp-line text-2xl"></i>
              </div>
              <div className="text-left">
                <div className="text-[10px] text-white/80 leading-none">Chat us on</div>
                <div className="text-base font-extrabold leading-tight">WhatsApp</div>
              </div>
            </a>
          </div>

          {/* Stats Bar */}
          <div className="flex flex-wrap justify-center gap-8 mb-0 pb-12 border-t border-white/10 pt-10">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-3xl font-extrabold text-[#D4AF37]">{s.value}</div>
                <div className="text-gray-400 text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Phone Mockup */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 flex justify-center">
          <div className="relative w-full max-w-4xl">
            <img
              src="https://readdy.ai/api/search-image?query=two%20modern%20smartphones%20showing%20video%20call%20app%20interface%20with%20people%20on%20screen%2C%20dark%20sleek%20phone%20design%2C%20floating%20on%20dark%20background%2C%20professional%20app%20mockup%20photography%2C%20red%20and%20gold%20UI%20accents%2C%20clean%20minimal%20style&width=900&height=500&seq=phonemock1&orientation=landscape"
              alt="CrownChat App"
              className="w-full object-cover object-top rounded-t-2xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a0a0a] via-transparent to-transparent rounded-t-2xl" />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20 bg-[#fdf8f8]">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-[#C41E3A]/10 text-[#C41E3A] text-xs font-semibold px-3 py-1 rounded-full mb-3">
              <i className="ri-star-line"></i> EVERYTHING YOU NEED
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">
              More Powerful Than WhatsApp &amp; WeChat
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-sm">
              CrownChat is built specifically for global trade — connecting buyers, suppliers, and resellers across USA, Canada, and the world.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-lg p-6 border border-gray-100 hover:border-[#C41E3A] transition-all group">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${f.color}`}>
                  <i className={`${f.icon} text-xl`}></i>
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-2">{f.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO CALL HIGHLIGHT */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-14 items-center">
            {/* Image */}
            <div className="lg:w-1/2">
              <div className="relative rounded-2xl overflow-hidden h-80 md:h-[460px]">
                <img
                  src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20business%20people%20on%20video%20call%20grid%20view%20on%20laptop%20screen%2C%20modern%20home%20office%20setting%2C%20professional%20video%20conference%20call%2C%20warm%20lighting%2C%20people%20smiling%20and%20engaged%2C%20global%20team%20meeting&width=700&height=460&seq=vcall1&orientation=landscape"
                  alt="CrownChat Video Call"
                  className="w-full h-full object-cover object-top"
                />
                {/* Floating call UI */}
                <div className="absolute bottom-4 left-4 right-4 bg-[#1a0a0a]/90 backdrop-blur-sm rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-white text-xs font-semibold">Live Video Call — 8 Participants</span>
                    </div>
                    <span className="text-[#D4AF37] text-xs font-bold">42:18</span>
                  </div>
                  <div className="flex gap-3 mt-3">
                    {[
                      { icon: "ri-mic-line", label: "Mute", active: true },
                      { icon: "ri-vidicon-line", label: "Video", active: true },
                      { icon: "ri-computer-line", label: "Share", active: false },
                      { icon: "ri-translate-2", label: "Translate", active: true },
                    ].map((btn) => (
                      <div key={btn.label} className="flex flex-col items-center gap-1">
                        <div className={`w-9 h-9 flex items-center justify-center rounded-full cursor-pointer ${btn.active ? "bg-white/20 text-white" : "bg-[#C41E3A] text-white"}`}>
                          <i className={`${btn.icon} text-sm`}></i>
                        </div>
                        <span className="text-[9px] text-gray-400">{btn.label}</span>
                      </div>
                    ))}
                    <div className="ml-auto flex flex-col items-center gap-1">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-600 text-white cursor-pointer">
                        <i className="ri-phone-line text-sm"></i>
                      </div>
                      <span className="text-[9px] text-gray-400">End</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:w-1/2">
              <div className="inline-flex items-center gap-2 bg-[#C41E3A]/10 text-[#C41E3A] text-xs font-semibold px-3 py-1 rounded-full mb-4">
                <i className="ri-vidicon-line"></i> HD VIDEO CALLING
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 leading-tight">
                See Your Suppliers.<br />
                <span className="text-[#C41E3A]">Trust What You Buy.</span>
              </h2>
              <p className="text-gray-500 text-sm mb-8 leading-relaxed">
                Video call your factory suppliers in China, your warehouse team in the USA, or your reseller network in Canada — all in crystal-clear HD. Just like WhatsApp and WeChat, but built for Crown Prime Mall business.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { icon: "ri-global-line", title: "Works Worldwide", desc: "Call anyone in 180+ countries — no international fees" },
                  { icon: "ri-hd-line", title: "1080p HD Quality", desc: "Sharp, clear video even on mobile data connections" },
                  { icon: "ri-group-line", title: "Up to 100 People", desc: "Host large supplier meetings or reseller training sessions" },
                  { icon: "ri-translate-2", title: "Live Translation", desc: "AI translates speech in real-time across 50+ languages" },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4 items-start">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#C41E3A]/10 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-[#C41E3A]`}></i>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">{item.title}</div>
                      <div className="text-gray-400 text-xs mt-0.5">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-20 bg-[#fdf8f8]">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-3">How CrownChat Compares</h2>
            <p className="text-gray-500 text-sm">Built for global trade — more than just messaging</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="grid grid-cols-4 bg-[#1a0a0a] text-white text-xs font-bold">
              <div className="p-4">Feature</div>
              <div className="p-4 text-center text-[#D4AF37]">CrownChat</div>
              <div className="p-4 text-center text-[#25D366]">WhatsApp</div>
              <div className="p-4 text-center text-blue-400">WeChat</div>
            </div>
            {[
              { feature: "HD Video Calls", crown: true, whatsapp: true, wechat: true },
              { feature: "Group Calls (100+)", crown: true, whatsapp: false, wechat: false },
              { feature: "Real-Time Translation", crown: true, whatsapp: false, wechat: false },
              { feature: "In-Chat Shopping", crown: true, whatsapp: false, wechat: true },
              { feature: "Supplier Verification", crown: true, whatsapp: false, wechat: false },
              { feature: "Order Notifications", crown: true, whatsapp: false, wechat: false },
              { feature: "USA & Canada App Store", crown: true, whatsapp: true, wechat: false },
              { feature: "Business Trade Focus", crown: true, whatsapp: false, wechat: false },
            ].map((row, i) => (
              <div key={row.feature} className={`grid grid-cols-4 text-sm border-t border-gray-50 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                <div className="p-4 text-gray-700 font-medium text-xs">{row.feature}</div>
                {[row.crown, row.whatsapp, row.wechat].map((val, j) => (
                  <div key={j} className="p-4 flex justify-center">
                    {val ? (
                      <div className="w-5 h-5 flex items-center justify-center bg-green-100 rounded-full">
                        <i className="ri-check-line text-green-600 text-xs"></i>
                      </div>
                    ) : (
                      <div className="w-5 h-5 flex items-center justify-center bg-gray-100 rounded-full">
                        <i className="ri-close-line text-gray-400 text-xs"></i>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-3">What Our Users Say</h2>
            <p className="text-gray-500 text-sm">Buyers, suppliers, and resellers across USA &amp; Canada</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-[#fdf8f8] rounded-xl p-6 border border-gray-100">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-[#D4AF37] text-sm"></i>
                    </div>
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-5 italic">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover object-top border-2 border-[#D4AF37]" />
                  <div>
                    <div className="font-bold text-gray-900 text-sm">{t.name}</div>
                    <div className="text-xs text-gray-400">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DOWNLOAD CTA */}
      <section className="py-20 bg-gradient-to-r from-[#C41E3A] to-[#8B0000]">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-2xl mx-auto mb-6">
            <i className="ri-message-3-line text-white text-3xl"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            CrownChat is Coming Soon
          </h2>
          <p className="text-white/80 text-sm mb-8 max-w-xl mx-auto leading-relaxed">
            Available on the <strong>Apple App Store</strong> and <strong>Google Play Store</strong> for USA &amp; Canada users. Sign up below to be the first to know when we launch — and get 3 months free premium access.
          </p>

          {/* Notify Form */}
          {notified ? (
            <div className="bg-white/20 border border-white/30 text-white rounded-xl px-6 py-4 max-w-md mx-auto">
              <i className="ri-check-line mr-2"></i>
              <strong>You&apos;re on the list!</strong> We&apos;ll notify you the moment CrownChat launches.
            </div>
          ) : (
            <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-8">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email address"
                className="flex-1 px-5 py-3 rounded-full text-sm outline-none text-gray-800"
              />
              <button
                type="submit"
                className="bg-[#D4AF37] text-white px-6 py-3 rounded-full font-bold text-sm hover:bg-[#b8962e] transition-colors cursor-pointer whitespace-nowrap"
              >
                Notify Me
              </button>
            </form>
          )}

          {/* Store Badges */}
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#" className="flex items-center gap-3 bg-white/10 border border-white/20 text-white px-6 py-3 rounded-xl hover:bg-white/20 transition-colors cursor-pointer">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-apple-line text-2xl"></i>
              </div>
              <div className="text-left">
                <div className="text-[10px] text-white/60 leading-none">Coming to</div>
                <div className="font-bold text-sm">App Store</div>
              </div>
            </a>
            <a href="#" className="flex items-center gap-3 bg-white/10 border border-white/20 text-white px-6 py-3 rounded-xl hover:bg-white/20 transition-colors cursor-pointer">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-google-play-line text-2xl text-green-400"></i>
              </div>
              <div className="text-left">
                <div className="text-[10px] text-white/60 leading-none">Coming to</div>
                <div className="font-bold text-sm">Google Play</div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer mini */}
      <div className="bg-[#1a0a0a] py-6 text-center text-gray-500 text-xs">
        <Link to="/" className="text-[#D4AF37] hover:underline cursor-pointer">Crown Prime Mall</Link>
        &nbsp;&mdash;&nbsp;CrownChat &copy; 2026. Available in USA &amp; Canada.
        &nbsp;|&nbsp;
        <a href="https://wa.me/1234567890" target="_blank" rel="noopener noreferrer" className="text-[#25D366] hover:underline cursor-pointer">WhatsApp Us</a>
      </div>
    </div>
  );
};

export default CrownChatPage;
